"use client";
import { useProUser } from "@/contexts/pro-user-context";
import { useAnalyzedProperties } from "@/hooks/use-analyzed-properties";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUser } from "@/hooks/useUser";
import { authenticatedFetch } from "@/lib/auth";
import { trackClientError } from "@/lib/client-error";
import { getAnalysisLimit } from "@/lib/pricing";
import { parseZillowUrl } from "@/lib/property-analyzer/";
import { cn } from "@/lib/utils";
import { track } from "@masterkey/analytics/client";
import { Eye, Home, Link2 } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { AuthConfirmModal } from "./auth/auth-confirm-modal";
import { useGooglePopup } from "./auth/google-popup";
import {
  LoadingModal,
  type StreamUpdate,
} from "./property-analysis/loading-modal";
import { SubscriptionPlansModal } from "./subscription-plans-modal";
import { useToast } from "./ui/use-toast";

interface ListingUrlInputProps {
  onSubmit?: (url: string) => void;
  className?: string;
  hideAnalysisLimit?: boolean;
  initialUrl?: string;
  showExample?: boolean;
  experimentVariation?: string;
  isHero?: boolean;
}

interface AnalyzeResponse {
  success: boolean;
  message?: string;
  error?: string;
  details?: string;
  data?: {
    slug: string;
  };
}

interface LightAnalyzeResponse {
  error?: string;
  details?: string;
  analysis?: {
    masterkey_score: number;
    location_score: number;
    condition_score: number;
    value_score: number;
    quick_take: string;
    key_highlights: string[];
    potential_issues: string[];
    property_id: string;
    processing_duration_ms: number;
  };
}

// Internal component without context
function ListingUrlInput({
  onSubmit,
  className,
  hideAnalysisLimit,
  initialUrl,
  showExample,
  experimentVariation,
  isHero,
}: ListingUrlInputProps) {
  const [inputValue, setInputValue] = useState(initialUrl || "");
  const [xPos, setXPos] = useState(0);
  const [yPos, setYPos] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [address, setAddress] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [state, setState] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [propertySlug, setPropertySlug] = useState<string | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [isLightAnalysis, setIsLightAnalysis] = useState(false);
  const [propertyAddress, setPropertyAddress] = useState<{
    city: string;
    state: string;
    address: string;
  } | null>(null);
  const [streamUpdates, setStreamUpdates] = useState<StreamUpdate[]>([]);
  const [currentPhase, setCurrentPhase] = useState<string>("initialize");
  const [realProgress, setRealProgress] = useState(0);
  const eventSourceRef = useRef<EventSource | null>(null);

  const hasStartedAnalysis = useRef(false);
  const hasAttemptedInitialAnalysis = useRef(false);
  const analyzeApiCalled = useRef(false);

  const { toast } = useToast();
  const { user, isLoading: isUserLoading } = useUser();
  const {
    hasReachedLimit,
    credits,
    isInitializing: isAnalysisInitializing,
  } = useAnalyzedProperties();
  const { tier } = useProUser();

  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingAnalysisUrl, setPendingAnalysisUrl] = useState<string | null>(
    null,
  );

  const isMobile = useIsMobile();

  const [analysisId, setAnalysisId] = useState<string | null>(null);

  const cleanupEventSource = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };

  useEffect(() => {
    return () => cleanupEventSource();
  }, []);

  const handleExampleClick = () => {
    track({
      event: "example-analysis-clicked",
      source: "listing-input",
      properties: {
        isLoggedIn: !!user,
        isMobile:
          typeof window !== "undefined" ? window.innerWidth < 640 : false,
        tier,
        experimentId: "hero-headline-test",
        experimentVariation,
      },
    });
  };

  const { handleSignIn } = useGooglePopup({
    onSignIn: () => {
      if (pendingUrl && propertyAddress && !hasStartedAnalysis.current) {
        hasStartedAnalysis.current = true;
        void startPropertyAnalysis(
          pendingUrl,
          propertyAddress,
          isLightAnalysis,
        );
      }
    },
    redirectPath: pendingAnalysisUrl
      ? `/?analyze=${encodeURIComponent(pendingAnalysisUrl)}`
      : undefined,
  });

  useEffect(() => {
    if (initialUrl && !hasAttemptedInitialAnalysis.current) {
      hasAttemptedInitialAnalysis.current = true;

      const handleInitialAnalysis = async () => {
        try {
          const address = await parseZillowUrl(initialUrl);
          setCity(address.city);
          setState(address.state);
          setAddress(address.address);
          setPropertyAddress(address);

          // Always show the loading modal for URL parameter cases
          setIsModalOpen(true);
          setPendingUrl(initialUrl);

          // If user is logged in, start analysis immediately
          if (user && !isUserLoading) {
            hasStartedAnalysis.current = true;
            void startPropertyAnalysis(initialUrl, address, false);
          }
          // For light analysis, start it immediately
          else if (isLightAnalysis) {
            hasStartedAnalysis.current = true;
            void handleLightAnalysis(initialUrl);
          }
          // Otherwise let the modal show sign-in UI (requiresSignIn will be true)
        } catch (error) {
          toast({
            title: "Analysis Failed",
            description: "Failed to analyze the property. Please try again.",
            variant: "destructive",
          });
          setIsModalOpen(false);
        }
      };

      void handleInitialAnalysis();
    }
  }, [initialUrl, user, isUserLoading, isLightAnalysis]);

  useEffect(() => {
    if (hasReachedLimit && isModalOpen) {
      window.history.replaceState({}, "", window.location.pathname);
      toast({
        title: "Analysis Limit Reached",
        description:
          tier === "plus"
            ? "You've reached your analysis limit. Upgrade to Pro for unlimited analyses."
            : "You've reached your free analysis limit. Upgrade to analyze more properties.",
        variant: "destructive",
      });
      setIsModalOpen(false);
      cleanupEventSource();
    }
  }, [hasReachedLimit, tier, isModalOpen]);

  useEffect(() => {
    if (hasReachedLimit) {
      setInputValue("");
    }
  }, [hasReachedLimit]);

  const isInputDisabled = isUserLoading || hasReachedLimit;

  const setupStream = (url: string) => {
    cleanupEventSource();

    setStreamUpdates([]);
    setRealProgress(0);
    setCurrentPhase("initialize");

    // Make a POST request to the streaming endpoint
    authenticatedFetch("/api/properties/analyze", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        url,
        stream: true,
      }),
    })
      .then((response) => {
        if (!response.ok || !response.body) {
          toast({
            title: "Analysis Failed",
            description: "Failed to start property analysis.",
            variant: "destructive",
          });
          setIsModalOpen(false);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        function processStream() {
          reader
            .read()
            .then(({ value, done }) => {
              if (done) {
                cleanupEventSource();
                return;
              }

              buffer += decoder.decode(value, { stream: true });

              // Process all complete messages in the buffer
              const messages = buffer.split("\n\n");
              buffer = messages.pop() || ""; // The last part might be incomplete

              for (const message of messages) {
                if (message.startsWith("data: ")) {
                  try {
                    const data = JSON.parse(message.slice(6)) as StreamUpdate;

                    setStreamUpdates((prevUpdates) => [...prevUpdates, data]);

                    if (data.phase) {
                      setCurrentPhase(data.phase);
                    }

                    if (data.progress !== undefined) {
                      setRealProgress((prevProgress) =>
                        // Only update if new progress is higher
                        data.progress !== undefined &&
                        data.progress > prevProgress
                          ? data.progress
                          : prevProgress,
                      );
                    }

                    if (data.phase === "complete") {
                      if (
                        data.data &&
                        typeof data.data === "object" &&
                        "slug" in data.data
                      ) {
                        setPropertySlug(data.data.slug as string);
                      }
                      setIsComplete(true);
                      return; // Stop reading the stream
                    }

                    if (data.error) {
                      toast({
                        title: "Analysis Error",
                        description: data.error,
                        variant: "destructive",
                      });

                      setIsModalOpen(false);
                      return; // Stop reading the stream
                    }
                  } catch (error) {
                    console.error("Error processing stream event", error);
                  }
                }
              }

              // Continue reading
              processStream();
            })
            .catch((error) => {
              console.error("Stream reading error:", error);
              if (!isComplete) {
                toast({
                  title: "Connection Error",
                  description:
                    "Lost connection to the analysis server. Please try again.",
                  variant: "destructive",
                });

                setIsModalOpen(false);
              }
            });
        }

        processStream();
      })
      .catch((error) => {
        console.error("Failed to start streaming:", error);
        toast({
          title: "Connection Error",
          description:
            "Failed to connect to the analysis server. Please try again.",
          variant: "destructive",
        });

        setIsModalOpen(false);
      });
  };

  const startPropertyAnalysis = async (
    url: string,
    parsedAddress?: { city: string; state: string; address: string },
    isLight = false,
  ) => {
    try {
      analyzeApiCalled.current = true;
      setIsLoading(true);

      track({
        event: isLight ? "light-analysis-started" : "listing-analysis-started",
        source: "listing-input",
        properties: {
          isLoggedIn: !!user,
          isRedirected: !!initialUrl,
          experimentId: "hero-headline-test",
          experimentVariation,
        },
      });

      const address = parsedAddress || (await parseZillowUrl(url));

      if (!isLight && !isLightAnalysis) {
        setupStream(url);
        return;
      }

      const endpoint = isLight
        ? "/api/properties/free-analysis"
        : "/api/properties/analyze";

      const fetchFn = isLight ? fetch : authenticatedFetch;

      const analysisResponse = await fetchFn(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          url: url,
        }),
      });

      const data = await analysisResponse.json();

      if (analysisResponse.status === 429) {
        const errorMessage = isLight
          ? "You've reached the limit for quick analyses. Sign in to get a detailed analysis and more features!"
          : "You've reached today's analysis limit. Come back tomorrow or upgrade to analyze unlimited properties.";
        throw new Error(errorMessage);
      }

      if (!analysisResponse.ok || (data.error && !isLight)) {
        throw new Error(
          data.details ||
            data.error ||
            "Failed to analyze property. Please try again later.",
        );
      }

      if (isLight) {
        const lightData = data as LightAnalyzeResponse;
        if (!lightData.analysis) {
          throw new Error("No analysis data returned");
        }
        setPropertySlug(null);
        setAnalysisId(lightData.analysis.property_id);
      } else {
        const fullData = data as AnalyzeResponse;
        const slug = fullData.data?.slug;
        if (!slug) {
          throw new Error("No property slug returned from analysis");
        }
        setPropertySlug(slug);
        setAnalysisId(null);
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));

      setIsComplete(true);
      track({
        event: isLight
          ? "light-analysis-completed"
          : "listing-analysis-completed",
        source: "listing-input",
        properties: {
          isLoggedIn: !!user,
        },
      });
      onSubmit?.(url);
      setInputValue("");
    } catch (error) {
      setIsModalOpen(false);
      setIsLoading(false);

      trackClientError(error, {
        tags: {
          component: "listing-url-input",
          function: "startPropertyAnalysis",
          type: "analyze_error",
        },
      });

      throw error;
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      cleanupEventSource();
      hasStartedAnalysis.current = false;
      analyzeApiCalled.current = false;
      setIsLightAnalysis(false);

      setStreamUpdates([]);
      setRealProgress(0);
      setCurrentPhase("initialize");

      // Don't clear URL parameter - we want to keep it for reload/navigation
    }
  }, [isModalOpen]);

  // Central effect to handle analysis start once loading states are resolved and modal is open
  useEffect(() => {
    // Only attempt to start analysis if modal is open and we have address data
    if (isModalOpen && propertyAddress && !analyzeApiCalled.current) {
      // For light analysis or logged in users under limit
      if ((user && !hasReachedLimit) || isLightAnalysis) {
        if (!hasStartedAnalysis.current) {
          analyzeApiCalled.current = true;
          hasStartedAnalysis.current = true;

          const urlToAnalyze = pendingUrl || initialUrl || inputValue;
          void startPropertyAnalysis(
            urlToAnalyze,
            propertyAddress,
            isLightAnalysis,
          );
        }
      }
      // Don't show auth modal here - let the loading modal handle auth state
    }
  }, [
    isModalOpen,
    propertyAddress,
    user,
    hasReachedLimit,
    isLightAnalysis,
    pendingUrl,
    initialUrl,
    inputValue,
  ]);

  const handleUpgradeClick = () => {
    if (tier === "plus") {
      window.location.href = "/app/settings?tab=subscription";
    } else {
      setIsPlansModalOpen(true);
    }
  };

  const handleLightAnalysis = async (url: string) => {
    try {
      // Don't start analysis if it's already in progress
      if (hasStartedAnalysis.current) {
        return;
      }

      setIsLightAnalysis(true);
      setPendingUrl(url);
      hasStartedAnalysis.current = true;

      // Track light analysis started
      track({
        event: "light-analysis-started",
        source: "listing-input",
        properties: {
          isLoggedIn: !!user,
          isRedirected: !!initialUrl,
        },
      });

      const address = await parseZillowUrl(url);
      setCity(address.city);
      setState(address.state);
      setAddress(address.address);
      setPropertyAddress(address);

      setIsModalOpen(true);

      // Use streaming for light analysis
      fetch("/api/properties/free-analysis", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          url,
          stream: true,
        }),
      })
        .then((response) => {
          if (!response.ok || !response.body) {
            hasStartedAnalysis.current = false;
            toast({
              title: "Analysis Failed",
              description: "Failed to start property analysis.",
              variant: "destructive",
            });
            setIsModalOpen(false);
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          function processStream() {
            reader
              .read()
              .then(({ value, done }) => {
                if (done) {
                  return;
                }

                buffer += decoder.decode(value, { stream: true });

                // Process all complete messages in the buffer
                const messages = buffer.split("\n\n");
                buffer = messages.pop() || ""; // The last part might be incomplete

                for (const message of messages) {
                  if (message.startsWith("data: ")) {
                    try {
                      const data = JSON.parse(message.slice(6)) as StreamUpdate;
                      setStreamUpdates((prevUpdates) => [...prevUpdates, data]);

                      if (data.phase) {
                        setCurrentPhase(data.phase);
                      }

                      if (data.progress !== undefined) {
                        setRealProgress((prevProgress) =>
                          // Only update if new progress is higher
                          data.progress !== undefined &&
                          data.progress > prevProgress
                            ? data.progress
                            : prevProgress,
                        );
                      }

                      if (data.phase === "complete") {
                        if (
                          data.data &&
                          typeof data.data === "object" &&
                          "property_id" in data.data
                        ) {
                          setAnalysisId(data.data.property_id as string);
                        }
                        setIsComplete(true);
                        return; // Stop reading the stream
                      }

                      if (data.error) {
                        hasStartedAnalysis.current = false;
                        toast({
                          title: "Analysis Error",
                          description: data.error,
                          variant: "destructive",
                        });

                        setIsModalOpen(false);
                        return; // Stop reading the stream
                      }
                    } catch (error) {
                      console.error("Error processing stream event", error);
                    }
                  }
                }

                // Continue reading
                processStream();
              })
              .catch((error) => {
                console.error("Stream reading error:", error);
                if (!isComplete) {
                  hasStartedAnalysis.current = false;
                  toast({
                    title: "Connection Error",
                    description:
                      "Lost connection to the analysis server. Please try again.",
                    variant: "destructive",
                  });

                  setIsModalOpen(false);
                }
              });
          }

          processStream();
        })
        .catch((error) => {
          console.error("Failed to start streaming:", error);
          hasStartedAnalysis.current = false;
          toast({
            title: "Connection Error",
            description:
              "Failed to connect to the analysis server. Please try again.",
            variant: "destructive",
          });

          setIsModalOpen(false);
        });
    } catch (error) {
      setIsModalOpen(false);
      hasStartedAnalysis.current = false;
      trackClientError(error, {
        tags: {
          component: "listing-url-input",
          function: "handleLightAnalysis",
          type: "submit_error",
        },
      });

      if (error instanceof Error && error.message.includes("limit")) {
        setShowAuthModal(true);
        toast({
          title: "Quick Analysis Limit Reached",
          description:
            "Sign in to unlock detailed property analysis and more features!",
          variant: "default",
        });
        return;
      }

      let errorMessage = "Failed to process the URL. Please try again.";
      if (error instanceof Error) {
        if (error.message.includes("Invalid Zillow URL")) {
          errorMessage = "Please provide a valid Zillow property listing URL";
        } else if (error.message.includes("Failed to fetch location facts")) {
          errorMessage = "Unable to get city information. Please try again.";
        } else if (
          error.message.includes("Unable to access property details")
        ) {
          errorMessage =
            "This property listing might be unavailable or require authentication.";
        }
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (
    urlOverrideOrEvent?: string | React.MouseEvent<HTMLButtonElement>,
  ) => {
    const urlToSubmit =
      typeof urlOverrideOrEvent === "string" ? urlOverrideOrEvent : inputValue;

    if (!urlToSubmit.startsWith("https://www.zillow.com/homedetails/")) {
      toast({
        title: "Invalid URL",
        description:
          "Please provide a valid Zillow property listing URL (e.g., https://www.zillow.com/homedetails/...)",
        variant: "destructive",
      });
      return;
    }

    try {
      const address = await parseZillowUrl(urlToSubmit);
      setCity(address.city);
      setState(address.state);
      setAddress(address.address);
      setPropertyAddress(address);

      // If user has reached their limit, show upgrade modal
      if (hasReachedLimit) {
        handleUpgradeClick();
        return;
      }

      // Different flow for manual URL entry vs URL parameter
      // If user is not logged in and this is a manual entry (not from URL parameter),
      // show the auth modal first
      if (!user && !isUserLoading && !initialUrl) {
        track({
          event: "analysis-login-prompt",
          source: "listing-input",
          properties: {
            isLoggedIn: false,
          },
        });

        setPendingAnalysisUrl(urlToSubmit);
        setShowAuthModal(true);
        return;
      }

      // For logged in users or URL parameter cases, show the loading modal directly
      setIsModalOpen(true);
      setPendingUrl(urlToSubmit);

      // Start analysis immediately if logged in
      if (user && !isUserLoading) {
        hasStartedAnalysis.current = true;
        void startPropertyAnalysis(urlToSubmit, address, false);
      }
      // Otherwise the modal will show sign in UI
    } catch (error) {
      trackClientError(error, {
        tags: {
          component: "listing-url-input",
          function: "handleSubmit",
          type: "submit_error",
        },
      });

      track({
        event: "listing-analysis-error",
        source: "listing-input",
        properties: {
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });
      toast({
        title: "Error",
        description: "Failed to process the URL. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setXPos(e.clientX - rect.left);
    setYPos(e.clientY - rect.top);
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedContent = e.clipboardData.getData("text");
    if (pastedContent.startsWith("https://www.zillow.com/")) {
      e.preventDefault();
      setInputValue(pastedContent);
      handleSubmit(pastedContent);
    }
  };

  const getPlaceholderText = () => {
    if (!user?.id) return "Paste Zillow listing link";
    if (credits.remaining_credits === 0) {
      return tier === "plus"
        ? "Upgrade to Pro for unlimited analyses"
        : "Upgrade for more analyses";
    }
    return "Paste Zillow listing link";
  };

  const renderAnalysisLimit = () => {
    if (!user?.id || hideAnalysisLimit) return null;

    const limit = getAnalysisLimit(tier);
    const remaining = credits.remaining_credits;
    const shouldShowWarning = remaining === 1;

    return (
      <div
        className={cn(
          "flex items-center gap-2 mb-2 text-sm",
          "sm:justify-end justify-center",
        )}
      >
        {shouldShowWarning && (
          <div className="flex items-center gap-1.5">
            {Array.from({
              length: limit === Number.POSITIVE_INFINITY ? 3 : limit,
            }).map((_, index) => (
              <div
                key={`analysis-${index}-${remaining}`}
                className={cn(
                  "w-[22px] h-[22px] rounded-md flex items-center justify-center transition-all duration-300",
                  index < remaining
                    ? "bg-canary/90 shadow-sm"
                    : "bg-gray-100 shadow-sm",
                )}
              >
                <Home
                  className={cn(
                    "w-4 h-4",
                    index < remaining ? "text-smokeyBlack" : "text-gray-400",
                  )}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={cn("max-w-xl relative", className)}>
        {renderAnalysisLimit()}
        <div className="bg-white rounded-xl shadow-xl p-2">
          <div
            className={cn(
              "flex",
              "sm:flex-row sm:items-center",
              isHero ? "flex-col gap-2" : "flex-row items-center",
            )}
          >
            <div className="flex-1 relative order-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="url"
                className={cn(
                  "w-full pl-10 font-semibold text-[16px] sm:text-lg sm:pl-12 pr-4 h-[52px] border-0 focus:ring-2 focus:ring-yellow-400 rounded-lg",
                  isInputDisabled && "cursor-not-allowed opacity-75",
                )}
                placeholder={getPlaceholderText()}
                value={inputValue}
                onChange={(e) =>
                  !isInputDisabled && setInputValue(e.target.value)
                }
                onPaste={(e) => !isInputDisabled && handlePaste(e)}
                disabled={isInputDisabled}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                onClick={() => {
                  if (hasReachedLimit) {
                    handleUpgradeClick();
                  }
                }}
              />
            </div>
            <button
              className={cn(
                "bg-yellow-400 text-gray-900 px-3 sm:px-6 h-[52px] rounded-lg font-bold relative overflow-hidden group hover:shadow-lg transition-all duration-500",
                isHero ? "w-full sm:w-auto" : "",
                "order-2 sm:order-3",
                "sm:ml-2",
                (isLoading || isInputDisabled) &&
                  "opacity-90 cursor-not-allowed",
              )}
              onMouseMove={handleMouseMove}
              onClick={hasReachedLimit ? handleUpgradeClick : handleSubmit}
              disabled={
                isInputDisabled ||
                (credits.remaining_credits > 0 &&
                  (isLoading || !inputValue.trim()))
              }
              type="button"
            >
              <span className="flex items-center gap-2 justify-center">
                {isLoading
                  ? "Analyzing..."
                  : hasReachedLimit
                    ? "Upgrade"
                    : isHero
                      ? "Start Your Free Analysis"
                      : "Analyze"}
              </span>
              <span
                className="absolute inset-0 rounded-r-full bg-[radial-gradient(circle_at_var(--xPos,_0%)_var(--yPos,_0%),rgba(255,255,255,0.25)_0%,transparent_50%)] opacity-0 group-hover:opacity-100 transition-opacity"
                style={
                  {
                    "--xPos": `${xPos}px`,
                    "--yPos": `${yPos}px`,
                  } as React.CSSProperties
                }
              />
            </button>
            {showExample && (
              <Link
                href="/properties/1825-s-16th-st-philadelphia-newbold-pa-19145"
                onClick={handleExampleClick}
                className={cn(
                  "hidden sm:flex items-center gap-2 ml-2 px-4 h-[52px] bg-white text-gray-600 rounded-lg font-semibold transition-all duration-300",
                  "border border-gray-200 shadow-sm",
                  "group hover:border-yellow-400/50 hover:bg-yellow-50/50 hover:text-gray-900 hover:shadow",
                  "order-3 sm:order-2",
                )}
              >
                See Example
              </Link>
            )}
          </div>
          {showExample && (
            <div className="sm:hidden mt-3 px-2 order-3">
              <Link
                href="/properties/1825-s-16th-st-philadelphia-newbold-pa-19145"
                onClick={handleExampleClick}
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-white text-gray-600 rounded-lg font-semibold transition-all duration-300 border border-gray-200 shadow-sm group hover:border-yellow-400/50 hover:bg-yellow-50/50 hover:text-gray-900 hover:shadow"
              >
                <Eye className="w-5 h-5 text-gray-500 group-hover:text-yellow-500 transition-colors" />
                <span>See Example</span>
              </Link>
            </div>
          )}
        </div>
      </div>

      {city && state && (
        <LoadingModal
          isOpen={isModalOpen}
          city={city}
          state={state}
          address={address}
          isComplete={isComplete}
          propertySlug={propertySlug}
          analysisId={analysisId}
          isLightAnalysis={isLightAnalysis}
          requiresSignIn={!user && !isLightAnalysis}
          streamUpdates={streamUpdates}
          currentPhase={currentPhase}
          realProgress={realProgress}
          onSignInClick={handleSignIn}
          onClose={() => {
            setIsModalOpen(false);
            setInputValue("");
            cleanupEventSource();
            hasStartedAnalysis.current = false;
            analyzeApiCalled.current = false;

            // Only clear URL parameter if this was from a URL parameter
            if (initialUrl) {
              window.history.replaceState({}, "", window.location.pathname);
            }
          }}
        />
      )}

      <AuthConfirmModal
        isOpen={showAuthModal}
        isLoading={false}
        onContinue={handleSignIn}
        onDismiss={() => {
          setShowAuthModal(false);
        }}
        onLightAnalysis={async () => {
          if (!pendingAnalysisUrl && !pendingUrl) return;

          // Use the appropriate URL, ensuring it's not null
          const urlToAnalyze = (pendingAnalysisUrl || pendingUrl) as string;

          setShowAuthModal(false);

          // Call handleLightAnalysis to use the free-analysis API
          try {
            setIsLightAnalysis(true);
            void handleLightAnalysis(urlToAnalyze);
          } catch (error) {
            toast({
              title: "Analysis Error",
              description: "Failed to start light analysis.",
              variant: "destructive",
            });
          }
        }}
        title="Start Your Free Analysis"
        description={`Sign in to analyze your ${city || ""} property and discover potential savings on your purchase. Your personalized report will be ready in under a minute.`}
        continueButtonText="Continue with Google"
        redirectPath={
          pendingAnalysisUrl
            ? `/?analyze=${encodeURIComponent(pendingAnalysisUrl)}`
            : undefined
        }
      />

      {tier === "free" && (
        <SubscriptionPlansModal
          isOpen={isPlansModalOpen}
          onClose={() => setIsPlansModalOpen(false)}
        />
      )}
    </>
  );
}

export { ListingUrlInput };
